import { trpc } from "@/lib/trpc";
import { COOKIE_NAME, UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { startLogin } from "./const";
import "./index.css";

const legacyImageFallback = `data:image/svg+xml,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 960 540" role="img" aria-label="Bharat Bulk SMS visual">
    <defs>
      <linearGradient id="background" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#071a3b"/>
        <stop offset="1" stop-color="#102f66"/>
      </linearGradient>
      <linearGradient id="accent" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stop-color="#ff6b2c"/>
        <stop offset="1" stop-color="#ffb11b"/>
      </linearGradient>
    </defs>
    <rect width="960" height="540" fill="url(#background)"/>
    <g opacity=".12" stroke="#fff"><path d="M0 90h960M0 180h960M0 270h960M0 360h960M0 450h960"/><path d="M160 0v540M320 0v540M480 0v540M640 0v540M800 0v540"/></g>
    <rect x="128" y="120" width="704" height="300" rx="24" fill="#ffffff0d" stroke="#ffffff33"/>
    <rect x="168" y="164" width="248" height="22" rx="11" fill="url(#accent)"/>
    <rect x="168" y="212" width="412" height="16" rx="8" fill="#ffffff99"/>
    <rect x="168" y="244" width="318" height="16" rx="8" fill="#ffffff55"/>
    <g fill="url(#accent)"><rect x="168" y="320" width="74" height="52" rx="10"/><rect x="270" y="294" width="74" height="78" rx="10"/><rect x="372" y="258" width="74" height="114" rx="10"/><rect x="474" y="308" width="74" height="64" rx="10"/><rect x="576" y="230" width="74" height="142" rx="10"/></g>
    <circle cx="734" cy="202" r="38" fill="#25d366" opacity=".9"/><path d="M720 203l10 9 20-24" fill="none" stroke="#fff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>
    <text x="168" y="460" font-family="Arial, sans-serif" font-size="24" fill="#ffffffcc">Business messaging workspace</text>
  </svg>
`)}`;

if (typeof document !== "undefined") {
  document.addEventListener(
    "error",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement) || target.dataset.legacyFallbackApplied) return;
      if (!/(\/manus-storage\/|\/cdn\/)/.test(target.currentSrc || target.src)) return;

      target.dataset.legacyFallbackApplied = "true";
      target.removeAttribute("srcset");
      target.alt = "Bharat Bulk SMS illustrative graphic";
      target.src = legacyImageFallback;
    },
    true,
  );
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  startLogin();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      headers() {
        // Preview auto-login fallback: when the browser blocks iframe cookies
        // (Safari ITP / private browsing / WebView), the runtime mirrors the
        // session into sessionStorage so we can forward it as a Bearer token.
        // The regular OAuth cookie flow keeps working and takes priority server-side.
        try {
          const raw = sessionStorage.getItem("manus-cookie");
          if (raw) {
            const prefix = `${COOKIE_NAME}=`;
            const pair = raw.split(";").find(s => s.trim().startsWith(prefix));
            const token = pair?.trim().slice(prefix.length);
            if (token) {
              return { Authorization: `Bearer ${token}` };
            }
          }
        } catch {
          // sessionStorage unavailable
        }
        return {};
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
